"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Field,
  TextInput,
  RememberedTextInput,
  RememberedNamesNote,
  TextArea,
  SelectInput,
  GuardedPrintButton,
  CompletionPanel,
  TableScroll,
  PrintFooter,
  trackToolResult,
  EvidenceFields,
  EvidencePrint,
  evidenceRequiredItems,
  useSavedEvidence,
  type Evidence,
} from "@/components/tools/ui";

/* A toolbox talk is a short (5-15 min), focused safety briefing delivered to a
   crew before or during work. It is not a training course and does not replace a
   RAMS briefing — it reinforces one specific risk. The presets below are grounded
   in the RamsDocs knowledge bank (HSE guidance + UK H&S sources); each is
   editable after selection so the deliverer can make it site-specific. */

interface TalkContent {
  keyMessage: string;
  whyItMatters: string;
  talkingPoints: string[];
  questions: string[];
  checkToday: string[];
}

interface Preset extends TalkContent {
  id: string;
  topic: string;
}

const PRESETS: Preset[] = [
  {
    id: "height-ladders",
    topic: "Working at height & ladders",
    keyMessage:
      "Falls from height are the biggest killer in construction — pick the right access for the job, not whatever is to hand.",
    whyItMatters:
      "A fall from a ladder or platform can kill or leave you with life-changing injuries. Most ladder falls happen on short jobs people thought were 'quick' — the ladder slips, isn't tied, or wasn't the right kit for the work in the first place.",
    talkingPoints: [
      "Ladders are for short-duration, light work only. If you'll be up there a while, or need both hands and to reach out, get a tower, podium or MEWP instead.",
      "A ladder used for access must be tied and must rise at least 1 m above the landing so there's a handhold to get on and off.",
      "Pre-use check every day: stiles, feet, rungs, locking mechanisms. No homemade or domestic-grade ladders on site.",
      "Three points of contact, belt buckle inside the stiles — don't overreach. Move the ladder rather than stretching for the last bit.",
      "Set on firm, level ground at roughly 75° (the 1-in-4 rule). Don't rest against fragile surfaces, fittings or glazing.",
    ],
    questions: [
      "On today's tasks, is a ladder actually the right choice — or would a tower or podium be safer?",
      "Who's checked their ladder this morning, and what would you reject one for?",
      "Where on this site is the ground too soft or sloped to foot a ladder safely?",
    ],
    checkToday: [
      "Every ladder in use gets a pre-use check before first use today.",
      "Access ladders are tied and extend 1 m above the landing.",
      "Anyone doing prolonged or two-handed work at height has the right platform, not a ladder.",
    ],
  },
  {
    id: "silica-dust",
    topic: "Silica dust (RCS)",
    keyMessage:
      "Cutting concrete, brick, stone or mortar dry will give you silicosis — wet-cut or extract it, every time.",
    whyItMatters:
      "Respirable crystalline silica is invisible and the damage is permanent: silicosis, COPD and lung cancer. It builds up over years from 'just a few cuts' done dry. A dust mask alone is not a control — it's the last line, and only works if it fits.",
    talkingPoints: [
      "Wet cutting cuts airborne silica by roughly 5-10 times. Keep water at the blade for the whole cut — a quick splash at the start doesn't count.",
      "Where water isn't practical, use on-tool (LEV) extraction fitted to the grinder, drill or saw and keep the filter maintained.",
      "RPE is a last resort on top of suppression, never instead of it. FFP3 for silica tasks, and it must be face-fit tested to you — stubble breaks the seal.",
      "Never dry sweep. Use a class-H vacuum or damp methods to clear settled dust.",
      "If you can see a dust cloud, the control has failed — stop and sort it before carrying on.",
    ],
    questions: [
      "Which cutting and chasing tasks today need water or extraction set up first?",
      "Who here has been face-fit tested for their FFP3, and when?",
      "How are we clearing settled dust at the end — and is anyone reaching for a brush?",
    ],
    checkToday: [
      "Water suppression or on-tool extraction is fitted and working on every cutting/grinding/chasing tool.",
      "Operators wearing FFP3 are face-fit tested and clean-shaven on the seal.",
      "No dry sweeping — vacuum or damp clean-up only.",
    ],
  },
  {
    id: "havs",
    topic: "Hand-arm vibration (HAVS)",
    keyMessage:
      "Vibrating tools damage your hands permanently — watch the trigger time, not the clock on the job.",
    whyItMatters:
      "HAVS and carpal tunnel syndrome are irreversible: numb, tingling, white fingers that don't grip and don't recover. It's the trigger time — the time the tool is actually buzzing in your hands — that does the harm, and it adds up across every tool you touch in a day.",
    talkingPoints: [
      "It's trigger time that counts, not how long the job takes. Plan to keep tools in your hands for as little time as possible.",
      "Rotate the heavy-vibration jobs around the crew so no one soaks up the whole shift's exposure.",
      "Use the lowest-vibration tool that'll do the work, keep blades and bits sharp, and let the tool do the cutting — don't force it.",
      "As a rule of thumb, regular hammer-action tools beyond ~15 min/day, or rotary tools beyond ~1 hour/day, mean exposure needs proper control.",
      "Tingling or numbness lasting more than 10 minutes after you put a tool down is an early warning — report it, don't ignore it.",
    ],
    questions: [
      "Which tasks today are the high-vibration ones, and how do we share them out?",
      "Has anyone noticed tingling or numb fingers after work lately?",
      "Are our tools sharp and serviced, or are we fighting blunt kit?",
    ],
    checkToday: [
      "High-vibration tasks are rotated, not dumped on one person.",
      "Tools are sharp/serviced and the lowest-vibration option for the job.",
      "Anyone with tingling or numbness reports it to the supervisor today.",
    ],
  },
  {
    id: "noise",
    topic: "Noise at work",
    keyMessage:
      "Noise-induced hearing loss is permanent and painless until it's done — protect your ears before it's too loud to talk.",
    whyItMatters:
      "Once your hearing is gone it doesn't come back, and tinnitus can ruin sleep for life. A simple test: if you have to shout to be heard at arm's length, the noise is at a level that needs controlling.",
    talkingPoints: [
      "Cut noise at source first — quieter tools, damping, keeping people out of the noisy area. Hearing protection is the last resort, not the first move.",
      "From 85 dB(A) hearing protection is mandatory and the area becomes a hearing protection zone — ear protection on, every time, no exceptions.",
      "Protection only works if it fits and you wear it the whole time. Pulling a plug out 'for a sec' to chat throws away most of the protection.",
      "Pick the right type for the noise — and don't over-protect either, or you can't hear plant, alarms or shouts of warning.",
      "Impulse noise — cartridge tools, breakers, piling — does damage in a single bang, so peak controls matter too.",
    ],
    questions: [
      "Where are the hearing protection zones on this site today?",
      "Could any of today's noisy jobs be done with quieter kit or methods?",
      "Does everyone have protection that actually fits — and are they wearing it the whole time?",
    ],
    checkToday: [
      "Hearing protection zones are signed and protection is worn within them.",
      "Quieter methods/tools are used where reasonably practicable.",
      "Anyone struggling to hear warnings or alarms flags it.",
    ],
  },
  {
    id: "manual-handling",
    topic: "Manual handling",
    keyMessage:
      "Most workplace injuries are manual handling — think TILE and use the kit before you use your back.",
    whyItMatters:
      "Manual handling is the most common cause of non-fatal injuries at work. Back, shoulder and knee damage builds up from repeated awkward lifts and ends careers. The fix is rarely 'lift more carefully' — it's avoiding or mechanising the lift.",
    talkingPoints: [
      "Think TILE before a lift: Task, Individual, Load, Environment. If any one of those is wrong, change it before you lift.",
      "Use the mechanical aid — trolley, pallet truck, hoist, wheelbarrow — wherever you can. Two-person lift if a load is awkward or heavy.",
      "Plan the route first: clear it, know where the load's going, and set it down at waist height where possible.",
      "Lift technique: stable stance, load close to the body, bend the knees not the back, turn your feet — never twist your spine.",
      "Carrying a load more than ~10 m, or pushing/pulling above shoulder or below knee height, sharply raises the injury risk — rethink the method.",
    ],
    questions: [
      "Which loads today are too heavy or awkward to lift safely by hand?",
      "What mechanical aids do we have on site, and are they where we need them?",
      "Where are the trip hazards on the carrying routes we'll use?",
    ],
    checkToday: [
      "Heavy or awkward loads use a mechanical aid or a two-person lift.",
      "Carrying routes are cleared and the drop point is sorted before lifting.",
      "No one twists the spine under load — feet turn, not the back.",
    ],
  },
  {
    id: "slips-housekeeping",
    topic: "Slips, trips & housekeeping",
    keyMessage:
      "A tidy site is a safe site — clear as you go, because the mess you leave is the next person's accident.",
    whyItMatters:
      "Slips and trips are one of the commonest causes of injury on site, and a cluttered area hides bigger hazards — open voids, trailing leads, fuel for a fire and blocked escape routes. A messy site also quietly tells everyone that safety doesn't matter here.",
    talkingPoints: [
      "Clear as you go. Don't let offcuts, packaging and waste pile up to be 'sorted later' — later is when someone trips.",
      "Trailing leads and hoses get routed overhead or covered, never left snaking across walkways.",
      "Keep walkways, stairs and access routes clear — they're also your escape routes if there's a fire.",
      "Spills (water, mud, oil, fuel) get dealt with straight away, not walked round.",
      "End-of-day clearing in a part-built structure is high risk — open voids and missing handrails near a 'quick sweep'. Stay switched on right to the end.",
    ],
    questions: [
      "Where on our patch is waste already building up, and whose is it?",
      "Are any leads or hoses crossing a walkway right now?",
      "If there were a fire, is our escape route clear of clutter?",
    ],
    checkToday: [
      "Each gang clears its own waste as it goes — not at the end of the week.",
      "Leads and hoses are routed off walkways.",
      "Access and escape routes are kept clear all day, including end-of-shift.",
    ],
  },
  {
    id: "ppe",
    topic: "PPE — wear it right",
    keyMessage:
      "PPE is the last line of defence, not the first — and it only protects you if it fits and you wear it.",
    whyItMatters:
      "PPE doesn't remove a hazard, it only reduces how badly it hurts you when something goes wrong — so it's what's left after the real controls. Worn loose, damaged, or wrong for the job, it gives a false sense of safety while you're still exposed.",
    talkingPoints: [
      "The site minimum is the 'big three' — head protection, safety footwear, hi-vis. On top of that, wear what the task and the RAMS call for.",
      "Right PPE for the right hazard: FFP3 and face-fit for dust, the correct gloves for the chemical, eye protection rated for the task.",
      "It has to fit and be in good condition. Check it before use; report damaged or worn PPE and swap it — don't make do.",
      "Watch for clashes — some goggles won't seal under a respirator, bulky gloves kill the dexterity you need. Sort the combination, don't drop a control.",
      "Store it properly so it's clean and undamaged next time. PPE left in the dirt isn't protecting anyone.",
    ],
    questions: [
      "What does today's work need beyond the site-minimum PPE?",
      "Is anyone's PPE damaged, worn out or not fitting properly?",
      "Where can you get a replacement on this site if yours fails?",
    ],
    checkToday: [
      "Everyone has the task-specific PPE the RAMS requires, not just the minimum.",
      "Damaged or ill-fitting PPE is reported and replaced before work starts.",
      "Tight-fitting RPE wearers are face-fit tested and clean-shaven on the seal.",
    ],
  },
  {
    id: "buried-services",
    topic: "Buried services",
    keyMessage:
      "Assume there's a live cable or gas main under every dig until you've proved otherwise — scan, plan, then dig safe.",
    whyItMatters:
      "Striking a buried cable can electrocute and burn you; hitting a gas main can cause fire or explosion; a water main can flood and collapse a trench. These strikes are nearly always avoidable — they happen when people dig before they've located what's down there.",
    talkingPoints: [
      "Get the service drawings and a permit to dig before anyone breaks ground. No plans, no permit, no digging.",
      "Use a CAT and Genny, operated by someone competent, to trace and mark services — and re-scan as you go down, not just once at the top.",
      "Hand-dig within 500 mm of any located service to expose it carefully — no machine buckets near a marked line.",
      "Watch the surface for clues: valve covers, manholes, road patching, marker tape. Assume cables in any street, footway or near a building.",
      "Know the emergency drill if you do strike something — what to do, who to call, and how to keep everyone clear.",
    ],
    questions: [
      "Do we have current service drawings and a permit to dig for today's excavation?",
      "Who's scanning, and when did they last re-scan as the dig got deeper?",
      "If we hit a gas main or cable, what do we do in the first 60 seconds?",
    ],
    checkToday: [
      "Service plans obtained and a permit to dig is in place before breaking ground.",
      "Competent operator has CAT-and-Genny scanned and marked the ground.",
      "Hand digging used within 500 mm of any located service.",
    ],
  },
  {
    id: "asbestos",
    topic: "Asbestos awareness",
    keyMessage:
      "If a building's pre-2000 and you're disturbing the fabric, stop and check the survey — asbestos kills slowly and you can't see it.",
    whyItMatters:
      "Asbestos still kills more workers than anything else in the UK each year. The fibres are invisible, the diseases take decades to show, and there's no cure. You cannot tell an asbestos material from a safe one by looking — only a survey and lab test can.",
    talkingPoints: [
      "Any building worked on that was built before 2000 may contain asbestos — assume it does until a survey says otherwise.",
      "A management survey is not enough before intrusive work. Refurbishment or demolition needs a refurbishment/demolition survey before the fabric is touched.",
      "Know what's on your site: read the asbestos register and the survey before you start, and know where the identified ACMs are.",
      "If you find or suspect a material that isn't in the survey — stop work immediately, don't disturb it, and report it. Don't drill, cut, sand or sweep it.",
      "Only licensed/trained people remove asbestos. Awareness means knowing how to avoid disturbing it — not how to take it out.",
    ],
    questions: [
      "Have we all seen the asbestos register and survey for this building?",
      "What would you do right now if you uncovered a suspect material?",
      "Is today's work intrusive — and is the right survey type in place for it?",
    ],
    checkToday: [
      "Asbestos register/survey reviewed before any intrusive work on pre-2000 fabric.",
      "Correct survey type (refurb/demo) is in place where the fabric is being disturbed.",
      "Anyone finding a suspect material stops, doesn't disturb it, and reports it.",
    ],
  },
  {
    id: "hot-works",
    topic: "Hot works & fire",
    keyMessage:
      "Sparks and flame start fires hours after you've finished — clear the area, keep a watch, and don't walk away early.",
    whyItMatters:
      "Welding, grinding, cutting and torch-on work throw sparks that smoulder out of sight and flare up long after the job's done. LPG is the single biggest fire risk on site. A site fire can destroy the job, trap people and kill.",
    talkingPoints: [
      "Get a hot works permit and clear combustibles from the work area before you strike a spark — sheet over anything you can't move.",
      "Suitable extinguishers at the point of work, checked and ready, before lighting up — typically CO2, water and a fire blanket.",
      "A dedicated fire watch, with no other job to do, watches throughout the work — and stays on for at least 60 minutes after the last flame.",
      "Final check before closing the permit: walk the area (and behind/below it), confirm nothing's smouldering, then sign off.",
      "LPG cylinders: stored upright and secured outside, hoses and fittings sound, turned off at the bottle when not in use.",
    ],
    questions: [
      "Is a hot works permit in place, and who's the fire watch today?",
      "What combustibles are near the work that we need to move or sheet?",
      "Where's the nearest extinguisher to where we'll be cutting?",
    ],
    checkToday: [
      "Hot works permit issued and the area cleared/sheeted before work.",
      "Correct extinguishers present and a dedicated fire watch assigned.",
      "Fire watch continues 60 min after the last flame before the permit is closed.",
    ],
  },
  {
    id: "plant-pedestrian",
    topic: "Plant & pedestrian segregation",
    keyMessage:
      "Keep people and moving plant apart — most of the deaths happen when someone's on foot where a machine is moving.",
    whyItMatters:
      "Being struck by moving plant is one of the biggest causes of death on construction sites, especially with reversing vehicles. The law requires sites to be set up so pedestrians and vehicles are kept separate — yet people climbing over barriers or cutting across haul routes is exactly how the fatalities happen.",
    talkingPoints: [
      "Use the designated pedestrian routes — don't cut across vehicle areas or climb over segregation barriers, even when it's quicker.",
      "Reversing is the danger zone. Stay out of it, make eye contact with the operator, and never assume they've seen you.",
      "Where people and plant must share space, a banksman controls it — agree the signals before anything moves.",
      "Operators: use exclusion zones, keep within speed limits, and don't move until you know where everyone on foot is.",
      "If the segregation has broken down — barriers moved, route blocked — stop and fix it, don't just work around it.",
    ],
    questions: [
      "Where do pedestrians and plant come closest together on our site today?",
      "Are the segregation barriers and routes all still in place this morning?",
      "Who's banksman where people and plant have to share space?",
    ],
    checkToday: [
      "Pedestrian and vehicle routes are separated and barriers are intact.",
      "A banksman controls any shared pedestrian/plant area.",
      "No one crosses vehicle routes or climbs barriers as a shortcut.",
    ],
  },
  {
    id: "coshh",
    topic: "COSHH basics",
    keyMessage:
      "Every hazardous substance needs its own assessment — the safety data sheet is the start, not the answer.",
    whyItMatters:
      "Solvents, resins, cements, fuels, dusts and fumes can poison, burn, sensitise or give you long-term lung and skin disease. A label or a data sheet tells you the substance is dangerous — it doesn't tell you how to use it safely here, which is what the COSHH assessment is for.",
    talkingPoints: [
      "Every product has its own COSHH assessment — not just a data sheet on file. Read it and know the controls before you open the tin.",
      "Know the routes in: breathing it, on the skin, swallowing it. Match the control to the route — ventilation/LEV, gloves, splash goggles.",
      "Use the least-harmful product that does the job, and the smallest amount. Substitution beats protection.",
      "Store and dispose of substances as the assessment says — right containers, no decanting into unlabelled bottles, controlled waste routes.",
      "Know the first-aid response for each substance before you start — eye wash, skin wash, what to do if it's swallowed.",
    ],
    questions: [
      "Which substances are we using today, and has everyone read the COSHH assessments?",
      "How does each one get into the body, and is our control aimed at the right route?",
      "Could we swap any product for a safer one and still get the job done?",
    ],
    checkToday: [
      "A COSHH assessment (not just a data sheet) is read for every substance in use.",
      "Controls match the exposure route — ventilation/LEV, correct gloves, eye protection.",
      "Substances are stored, labelled and disposed of as the assessment requires.",
    ],
  },
  {
    id: "heat-cold",
    topic: "Heat stress / cold weather",
    keyMessage:
      "Weather is a hazard that changes by the hour — plan for the heat or the cold before it catches you out.",
    whyItMatters:
      "Heat brings dehydration, heat exhaustion and lost concentration — and tired, dehydrated workers lose grip and coordination, so routine tasks turn dangerous. Cold and ice bring slips, numb hands and cold injuries. The first warm or cold days are worst, before anyone's acclimatised.",
    talkingPoints: [
      "Hot weather: drink water regularly before you're thirsty, take breaks in the shade, and watch your mates for dizziness, cramps or confusion.",
      "Pace heavy work for the cooler parts of the day where the programme allows, and don't push through if you're overheating.",
      "Cold weather: layer up, keep hands warm enough to grip, and grit/salt walkways and accesses against ice.",
      "Check the forecast before weather-sensitive work — pours, lifts, roofing, excavations — and have a stop/relocate trigger if it turns.",
      "Sun is a hazard too: cover up and use sun cream — skin cancer is an occupational risk for outdoor workers.",
    ],
    questions: [
      "What's the forecast for the rest of the shift, and does it change any of today's plan?",
      "Where's the shade/water (or warm break area) and is it stocked?",
      "Which jobs today should we move to a cooler/warmer part of the day?",
    ],
    checkToday: [
      "Water and shade (hot) or warm breaks and grit (cold) are available and used.",
      "Weather-sensitive tasks have a stop/relocate trigger agreed.",
      "Crew watch each other for heat or cold stress and speak up early.",
    ],
  },
  {
    id: "mental-health",
    topic: "Mental health & fatigue awareness",
    keyMessage:
      "It's okay to not be okay — fatigue and stress cause accidents, and there's confidential help one call away.",
    whyItMatters:
      "Construction workers are around three times more likely to die by suicide than the national average, and stress and fatigue are normalised on site rather than treated as real risks. A tired or distracted mind makes the mistakes that hurt people — yours or someone else's.",
    talkingPoints: [
      "Fatigue is a safety hazard. Long hours, poor sleep and the daily commute add up — if you're running on empty, say so before you make a mistake.",
      "Stress shows up as short temper, mistakes, not sleeping, or going quiet. Notice it in yourself and in your mates.",
      "Asking how someone's doing — and actually listening — costs nothing. Look out for the workmate who's gone off the radar.",
      "There's confidential, free help: the Lighthouse Construction Industry Charity runs a 24/7 helpline for workers and families.",
      "Speaking up about workload or pressure isn't weakness — it's how we stop a tired crew making a serious error.",
    ],
    questions: [
      "Is anyone running on too little sleep or too many hours to be safe today?",
      "Do you know how to reach the Lighthouse helpline or a mental health first aider on site?",
      "What's one thing about how we work that adds avoidable stress?",
    ],
    checkToday: [
      "Anyone too fatigued to work safely tells their supervisor.",
      "The Lighthouse 24/7 helpline number is shared and visible on site.",
      "Crew look out for each other and check in on anyone struggling.",
    ],
  },
];

const CUSTOM_HINTS: TalkContent = {
  keyMessage: "",
  whyItMatters: "",
  talkingPoints: ["", "", "", ""],
  questions: ["", "", ""],
  checkToday: ["", "", ""],
};

function fmtDate(iso: string): string {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function clone(c: TalkContent): TalkContent {
  return {
    keyMessage: c.keyMessage,
    whyItMatters: c.whyItMatters,
    talkingPoints: [...c.talkingPoints],
    questions: [...c.questions],
    checkToday: [...c.checkToday],
  };
}

interface KnowledgeCheck {
  question: string;
  answer: string;
}

const EMPTY_CHECKS: KnowledgeCheck[] = [
  { question: "", answer: "" },
  { question: "", answer: "" },
  { question: "", answer: "" },
];

export function ToolboxTalkBuilder() {
  // Header fields
  const [site, setSite] = useState("");
  const [presenter, setPresenter] = useState("");
  const [duration, setDuration] = useState("10");
  const [ramsRef, setRamsRef] = useState("");

  // Document control & sign-off
  const [evidence, setEvidence, clearEvidence] = useSavedEvidence();

  // Optional knowledge-check questions with their expected answers.
  const [knowledge, setKnowledge] = useState<KnowledgeCheck[]>(EMPTY_CHECKS);

  // Topic + editable content
  const [presetId, setPresetId] = useState("");
  const [topic, setTopic] = useState("");
  const [content, setContent] = useState<TalkContent>(clone(CUSTOM_HINTS));

  const resultTracked = useRef(false);

  const choosePreset = (id: string) => {
    setPresetId(id);
    if (id === "") {
      setTopic("");
      setContent(clone(CUSTOM_HINTS));
      return;
    }
    const p = PRESETS.find((x) => x.id === id);
    if (p) {
      setTopic(p.topic);
      setContent(clone(p));
    }
  };

  const ready = topic.trim() !== "" && content.keyMessage.trim() !== "";

  // Fire the funnel event once per visit when a usable talk first exists.
  useEffect(() => {
    if (ready && !resultTracked.current) {
      resultTracked.current = true;
      trackToolResult("toolbox-talk", { topic });
    }
  }, [ready, topic]);

  const setListItem = (key: "talkingPoints" | "questions" | "checkToday", i: number, value: string) =>
    setContent((c) => {
      const next = [...c[key]];
      next[i] = value;
      return { ...c, [key]: next };
    });

  const addListItem = (key: "talkingPoints" | "questions" | "checkToday") =>
    setContent((c) => ({ ...c, [key]: [...c[key], ""] }));

  const removeListItem = (key: "talkingPoints" | "questions" | "checkToday", i: number) =>
    setContent((c) => ({ ...c, [key]: c[key].filter((_, idx) => idx !== i) }));

  // Filter empties for the printed output.
  const printPoints = useMemo(() => content.talkingPoints.filter((p) => p.trim() !== ""), [content.talkingPoints]);
  const printQuestions = useMemo(() => content.questions.filter((q) => q.trim() !== ""), [content.questions]);
  const printChecks = useMemo(() => content.checkToday.filter((c) => c.trim() !== ""), [content.checkToday]);
  const printKnowledge = useMemo(
    () => knowledge.filter((k) => k.question.trim() !== ""),
    [knowledge],
  );

  // A proportionate core for a usable talk sheet: who/what + the message and at
  // least one talking point. The knowledge check and RAMS link stay optional.
  const requiredItems: { label: string; done: boolean }[] = [
    { label: "Topic title", done: topic.trim().length > 0 },
    { label: "Key message", done: content.keyMessage.trim().length > 0 },
    { label: "At least one talking point", done: printPoints.length > 0 },
    { label: "Presenter", done: presenter.trim().length > 0 },
    { label: "Site / project", done: site.trim().length > 0 },
    ...evidenceRequiredItems(evidence),
  ];
  const missingRequired = requiredItems.filter((i) => !i.done).map((i) => i.label);

  const setCheck = (i: number, patch: Partial<KnowledgeCheck>) =>
    setKnowledge((ks) => ks.map((k, idx) => (idx === i ? { ...k, ...patch } : k)));

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
      {/* ---- Builder ---- */}
      <div className="space-y-6 print-hide">
        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Talk details</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <Field label="Site / project">
              <RememberedTextInput store="sites" value={site} onChange={(e) => setSite(e.target.value)} placeholder="e.g. Plot 4, Mill Lane" />
            </Field>
            <Field label="Presenter">
              <RememberedTextInput store="people" value={presenter} onChange={(e) => setPresenter(e.target.value)} placeholder="Who is giving the talk" />
            </Field>
            <Field label="Estimated duration (minutes)" hint="A toolbox talk should run 5-15 minutes.">
              <SelectInput value={duration} onChange={(e) => setDuration(e.target.value)}>
                <option value="5">5 minutes</option>
                <option value="10">10 minutes</option>
                <option value="15">15 minutes</option>
              </SelectInput>
            </Field>
            <Field label="Related RAMS / permit references (optional)" className="sm:col-span-2" hint="The risk assessment, method statement or permit(s) this talk supports.">
              <TextInput value={ramsRef} onChange={(e) => setRamsRef(e.target.value)} placeholder="e.g. RAMS-2024-118 — Groundworks; Hot-works permit HW-204" />
            </Field>
          </div>
          <div className="mt-4">
            <RememberedNamesNote stores={["people", "sites"]} />
          </div>
        </div>

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Topic</h2>
          <p className="mt-1 text-sm text-muted">
            Pick a ready-made talk and edit it to fit your site, or start from a blank structure.
          </p>
          <div className="mt-4 grid gap-4">
            <Field label="Pick a talk from the library">
              <SelectInput value={presetId} onChange={(e) => choosePreset(e.target.value)}>
                <option value="">Custom topic (blank structure)…</option>
                {PRESETS.map((p) => (
                  <option key={p.id} value={p.id}>{p.topic}</option>
                ))}
              </SelectInput>
            </Field>
            <Field label="Topic title">
              <TextInput value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="e.g. Working near live overhead lines" />
            </Field>
            <Field label="Key message (one sentence)" hint="The single thing you want everyone to walk away with.">
              <TextArea value={content.keyMessage} onChange={(e) => setContent((c) => ({ ...c, keyMessage: e.target.value }))} placeholder="Say it in one plain sentence." />
            </Field>
            <Field label="Why it matters (the harm)" hint="Be honest about the consequence — that's what makes it land.">
              <TextArea value={content.whyItMatters} onChange={(e) => setContent((c) => ({ ...c, whyItMatters: e.target.value }))} placeholder="What actually happens to someone if this goes wrong." />
            </Field>
          </div>
        </div>

        <EditableList
          title="Talking points"
          hint="Concrete, site-level — 'do this, not that'. Aim for 4-6."
          items={content.talkingPoints}
          placeholder="A specific thing to do on site"
          onChange={(i, v) => setListItem("talkingPoints", i, v)}
          onAdd={() => addListItem("talkingPoints")}
          onRemove={(i) => removeListItem("talkingPoints", i)}
        />
        <EditableList
          title="Discussion questions"
          hint="Ask these — don't lecture. A talk that's all one-way doesn't change behaviour."
          items={content.questions}
          placeholder="A question to put to the crew"
          onChange={(i, v) => setListItem("questions", i, v)}
          onAdd={() => addListItem("questions")}
          onRemove={(i) => removeListItem("questions", i)}
        />
        <EditableList
          title="Check today"
          hint="The actions you want done on site as a result of this talk."
          items={content.checkToday}
          placeholder="Something to confirm on site today"
          onChange={(i, v) => setListItem("checkToday", i, v)}
          onAdd={() => addListItem("checkToday")}
          onRemove={(i) => removeListItem("checkToday", i)}
        />

        <div className="card shadow-soft p-6">
          <h2 className="text-xl">Knowledge check (optional)</h2>
          <p className="mt-1 text-sm text-muted">
            Up to three quick questions to confirm the crew took the message in. The expected answer is for the
            presenter — it prints as a &ldquo;check understanding&rdquo; block. Leave blank to skip.
          </p>
          <div className="mt-4 space-y-4">
            {knowledge.map((k, i) => (
              <div key={i} className="rounded-xl border border-line bg-mist/60 p-4">
                <span className="text-[0.8rem] font-bold uppercase tracking-wide text-muted">Question {i + 1}</span>
                <div className="mt-3 grid gap-3">
                  <Field label="Question">
                    <TextInput
                      value={k.question}
                      onChange={(e) => setCheck(i, { question: e.target.value })}
                      placeholder="e.g. When can a fire watch leave after hot works?"
                    />
                  </Field>
                  <Field label="Expected answer">
                    <TextInput
                      value={k.answer}
                      onChange={(e) => setCheck(i, { answer: e.target.value })}
                      placeholder="e.g. At least 60 minutes after the last flame, after a final check."
                    />
                  </Field>
                </div>
              </div>
            ))}
          </div>
        </div>

        <EvidenceFields value={evidence} onChange={setEvidence} onClear={clearEvidence} />
      </div>

      {/* ---- Preview + printable output ---- */}
      <div className="space-y-4">
        <div className="lg:sticky lg:top-20 lg:z-10">
          <CompletionPanel items={requiredItems} title="Talk completion" />
        </div>
        {ready ? (
          <>
            <div className="print-area card shadow-soft p-6 sm:p-8" id="toolbox-talk-sheet">
              {/* Title block */}
              <div className="text-[0.74rem] font-semibold uppercase tracking-wide text-emerald-deep">
                Toolbox talk
              </div>
              <h2 className="mt-1 text-2xl">{topic}</h2>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-[0.85rem] text-body sm:grid-cols-3">
                <div><span className="text-muted">Site:</span> {site || "—"}</div>
                <div><span className="text-muted">Presenter:</span> {presenter || "—"}</div>
                <div><span className="text-muted">Date:</span> {fmtDate(evidence.date) || "—"}</div>
                <div><span className="text-muted">Duration:</span> ~{duration} min</div>
                {ramsRef.trim() !== "" && (
                  <div className="col-span-2 sm:col-span-2"><span className="text-muted">Related RAMS / permits:</span> {ramsRef}</div>
                )}
              </div>

              {/* Key message callout */}
              <div className="mt-5 rounded-xl border border-risk-low/40 bg-emerald-tint px-5 py-4 text-emerald-deep">
                <div className="text-[0.72rem] font-semibold uppercase tracking-wide opacity-80">Key message</div>
                <div className="mt-1 font-display text-lg font-bold leading-snug">{content.keyMessage}</div>
              </div>

              {/* Why it matters */}
              {content.whyItMatters.trim() !== "" && (
                <div className="mt-5">
                  <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Why it matters</h3>
                  <p className="mt-1.5 text-[0.92rem] leading-relaxed text-body">{content.whyItMatters}</p>
                </div>
              )}

              {/* Talking points */}
              {printPoints.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Talking points</h3>
                  <ol className="mt-2 space-y-2">
                    {printPoints.map((p, i) => (
                      <li key={i} className="flex gap-3 text-[0.92rem] leading-relaxed text-body">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald text-[0.7rem] font-bold text-white">
                          {i + 1}
                        </span>
                        <span>{p}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Discussion questions */}
              {printQuestions.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Ask the crew</h3>
                  <ul className="mt-2 space-y-1.5">
                    {printQuestions.map((q, i) => (
                      <li key={i} className="flex gap-2 text-[0.92rem] leading-relaxed text-body">
                        <span className="text-emerald-deep">?</span>
                        <span>{q}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Check today */}
              {printChecks.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Check today</h3>
                  <ul className="mt-2 space-y-1.5">
                    {printChecks.map((c, i) => (
                      <li key={i} className="flex gap-2 text-[0.92rem] leading-relaxed text-body">
                        <span className="mt-0.5 text-emerald-deep">☐</span>
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Knowledge check */}
              {printKnowledge.length > 0 && (
                <div className="mt-5">
                  <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Check understanding</h3>
                  <ol className="mt-2 space-y-2.5">
                    {printKnowledge.map((k, i) => (
                      <li key={i} className="text-[0.92rem] leading-relaxed text-body">
                        <div className="flex gap-2">
                          <span className="font-semibold text-emerald-deep">Q{i + 1}.</span>
                          <span className="font-semibold text-ink">{k.question}</span>
                        </div>
                        {k.answer.trim() !== "" && (
                          <div className="mt-0.5 pl-7 text-muted">
                            <span className="font-semibold">Expected:</span> {k.answer}
                          </div>
                        )}
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              {/* Attendance sheet */}
              <div className="mt-7 border-t border-line pt-5">
                <h3 className="text-[0.9rem] font-bold uppercase tracking-wide text-ink">Attendance &amp; sign-off</h3>
                <p className="mt-1 text-[0.8rem] text-muted">
                  Everyone present signs to confirm they attended and understood this talk.
                </p>
                <TableScroll>
                  <table className="mt-3 w-full min-w-[480px] border-collapse text-[0.85rem]">
                    <thead>
                      <tr className="border-b-2 border-line text-left text-[0.72rem] uppercase tracking-wide text-muted">
                        <th className="w-8 py-1.5 pr-2 font-semibold">#</th>
                        <th className="py-1.5 pr-2 font-semibold">Name</th>
                        <th className="py-1.5 pr-2 font-semibold">Company</th>
                        <th className="py-1.5 font-semibold">Signature</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.from({ length: 12 }).map((_, i) => (
                        <tr key={i} className="border-b border-line/70">
                          <td className="py-3.5 pr-2 text-muted">{i + 1}</td>
                          <td className="py-3.5 pr-2"></td>
                          <td className="py-3.5 pr-2"></td>
                          <td className="py-3.5"></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </TableScroll>
              </div>

              <EvidencePrint value={evidence} briefing />

              <PrintFooter toolName="toolbox talk builder" />
            </div>

            <div className="print-hide">
              <GuardedPrintButton
                tool="toolbox-talk"
                label="Print / save talk + sheet as PDF"
                missing={missingRequired}
                warningTitle="This toolbox talk is missing core details"
              />
            </div>
          </>
        ) : (
          <div className="card border-dashed p-6 text-center text-sm text-muted">
            Pick a talk from the library (or enter a custom topic and key message) to build a printable talk
            sheet with an attendance and sign-off table.
          </div>
        )}
      </div>
    </div>
  );
}

function EditableList({
  title,
  hint,
  items,
  placeholder,
  onChange,
  onAdd,
  onRemove,
}: {
  title: string;
  hint: string;
  items: string[];
  placeholder: string;
  onChange: (i: number, v: string) => void;
  onAdd: () => void;
  onRemove: (i: number) => void;
}) {
  return (
    <div className="card shadow-soft p-6">
      <h2 className="text-xl">{title}</h2>
      <p className="mt-1 text-sm text-muted">{hint}</p>
      <div className="mt-4 space-y-2.5">
        {items.map((item, i) => (
          <div key={i} className="flex items-start gap-2">
            <span className="mt-2.5 w-5 shrink-0 text-right text-[0.8rem] font-semibold text-muted">{i + 1}</span>
            <TextArea
              rows={2}
              value={item}
              onChange={(e) => onChange(i, e.target.value)}
              placeholder={placeholder}
            />
            {items.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={`Remove item ${i + 1}`}
                className="mt-1.5 shrink-0 rounded-md p-1.5 text-muted hover:text-risk-veryhigh"
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m2 0v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={onAdd}
        className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-line-2 px-4 py-2 text-sm font-semibold text-ink hover:border-ink hover:bg-mist"
      >
        + Add another
      </button>
    </div>
  );
}
